import { useMemo, useState } from 'react'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Search } from 'lucide-react'
import { cn } from '@/lib/utils'
import type { Artist } from '@/types/models'

function invitingArtistChipName(a: Artist): string {
  if (a.firstName && a.lastName) {
    return `${a.firstName} ${a.lastName.charAt(0)}.`
  }
  return a.displayName
}

export function invitingArtistLookup(artists: Artist[], invitingArtistId?: string): Artist | undefined {
  if (!invitingArtistId) return undefined
  return artists.find((a) => a.id === invitingArtistId)
}

/** Compact “who invited them” summary for onboarding admin list/detail */
export function InvitingArtistAttribution({
  artists,
  invitingArtistId,
  alignRight = false,
}: {
  artists: Artist[]
  invitingArtistId?: string
  /** When true, right-align chip on row layouts */
  alignRight?: boolean
}) {
  const inviter = invitingArtistLookup(artists, invitingArtistId)

  if (!invitingArtistId) {
    return <p className="text-xs text-muted-foreground">Not set</p>
  }
  if (!inviter) {
    return (
      <p className="text-xs text-muted-foreground" title={invitingArtistId}>
        Unknown artist (removed or missing)
      </p>
    )
  }

  const avatarSrc =
    inviter.compressedAvatarUrl ||
    inviter.avatarUrl ||
    `https://ui-avatars.com/api/?name=${encodeURIComponent(invitingArtistChipName(inviter))}`

  return (
    <div className={cn('flex items-center gap-2', alignRight && 'sm:justify-end')}>
      <img src={avatarSrc} alt="" className="h-8 w-8 shrink-0 rounded-full object-cover bg-muted" />
      <span className="min-w-0 truncate text-sm font-medium text-foreground">{invitingArtistChipName(inviter)}</span>
    </div>
  )
}

export interface InvitingArtistPickerDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  artists: Artist[]
  selectedArtistId: string | null
  /** Single artist id replaces previous selection */
  onSelect: (artistId: string | null) => void
}

export function InvitingArtistPickerDialog({
  open,
  onOpenChange,
  artists,
  selectedArtistId,
  onSelect,
}: InvitingArtistPickerDialogProps) {
  const [searchQuery, setSearchQuery] = useState('')

  const filteredArtists = useMemo(() => {
    if (!searchQuery.trim()) return artists
    const query = searchQuery.toLowerCase()
    return artists.filter(
      (a) =>
        a.displayName?.toLowerCase().includes(query) ||
        a.firstName?.toLowerCase().includes(query) ||
        a.lastName?.toLowerCase().includes(query) ||
        a.location?.city?.toLowerCase().includes(query),
    )
  }, [artists, searchQuery])

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!next) setSearchQuery('')
        onOpenChange(next)
      }}
    >
      <DialogContent className="sm:max-w-md max-h-[90vh] overflow-hidden flex flex-col gap-0 p-0">
        <DialogHeader className="px-6 pt-6 pb-2 shrink-0">
          <DialogTitle>Inviting artist</DialogTitle>
          <DialogDescription>
            Optional — link this onboarding to an artist who referred or invited them.
          </DialogDescription>
        </DialogHeader>
        <div className="px-6 pb-4 space-y-3 flex flex-col min-h-0 flex-1">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground pointer-events-none" />
            <Input
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search artists…"
              autoFocus
              className="pl-9"
            />
          </div>
          <div className="rounded-md border min-h-[200px] max-h-[min(50vh,360px)] overflow-y-auto divide-y">
            {filteredArtists.length === 0 ? (
              <p className="p-8 text-center text-sm text-muted-foreground">No artists found</p>
            ) : (
              filteredArtists.map((artist) => {
                const selected = artist.id === selectedArtistId
                return (
                  <button
                    key={artist.id}
                    type="button"
                    className={cn(
                      'w-full flex items-center gap-3 px-3 py-3 text-left transition-colors hover:bg-muted/80',
                      selected && 'bg-primary/10',
                    )}
                    onClick={() => {
                      onSelect(artist.id === selectedArtistId ? null : artist.id)
                    }}
                  >
                    <img
                      src={
                        artist.compressedAvatarUrl ||
                        artist.avatarUrl ||
                        'https://ui-avatars.com/api/?name=' + encodeURIComponent(invitingArtistChipName(artist))
                      }
                      alt=""
                      className="h-10 w-10 rounded-full object-cover flex-shrink-0 bg-muted"
                    />
                    <div className="min-w-0 flex-1">
                      <p className="font-medium text-foreground truncate">{invitingArtistChipName(artist)}</p>
                      {artist.location?.city && (
                        <p className="text-xs text-muted-foreground truncate">{artist.location.city}</p>
                      )}
                      <p className="text-xs text-muted-foreground truncate">@{artist.displayName.replace(/^@/, '')}</p>
                    </div>
                    {selected && (
                      <span className="text-xs font-semibold text-primary shrink-0">Selected</span>
                    )}
                  </button>
                )
              })
            )}
          </div>
        </div>
        <div className="flex flex-shrink-0 flex-col-reverse gap-2 px-6 pb-6 pt-2 sm:flex-row sm:justify-between">
          <Button type="button" variant="outline" disabled={!selectedArtistId} onClick={() => onSelect(null)}>
            Clear selection
          </Button>
          <Button type="button" onClick={() => onOpenChange(false)}>
            Done
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  )
}
